import type { WeatherPayload, WeatherCode } from '../../types'

// WeatherStrip - 7-day Open-Meteo forecast for Pickering. Renders a row of
// seven mini-cards (weekday + glyph + min/max). When the upstream call fails
// after the cache has expired we show "Weather unavailable"; when we're
// serving stale-cache, we put a small "·" with an "Updated <time>" tooltip.

interface Props {
  data: WeatherPayload | undefined
  loading: boolean
  errored: boolean
}

const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const GLYPH: Record<WeatherCode, string> = {
  sun: '☀',
  partly: '⛅',
  cloud: '☁',
  rain: '🌧',
  storm: '⛈',
  snow: '❄',
  fog: '🌫'
}

export function WeatherStrip({ data, loading, errored }: Props) {
  return (
    <div className="bg-white border border-line rounded">
      <div className="px-5 py-3 border-b border-line flex items-baseline justify-between">
        <h2 className="font-display text-lg">Weather · Pickering</h2>
        {data?.stale && (
          <span
            className="text-[11px] text-ink-soft"
            title={`Updated ${new Date(data.fetchedAt).toLocaleString('en-GB')}`}
          >
            · stale
          </span>
        )}
      </div>
      <div className="p-4">
        {loading && (
          <div className="text-sm text-ink-soft font-mono">Loading…</div>
        )}
        {!loading && errored && (
          <div className="text-sm text-ink-soft italic">Weather unavailable.</div>
        )}
        {!loading && !errored && data && (
          <div className="grid grid-cols-7 gap-1.5 text-center text-xs">
            {data.days.map((d, i) => {
              const dt = new Date(d.date + 'T00:00:00')
              const wd = WEEKDAY_SHORT[dt.getDay()] ?? ''
              return (
                <div
                  key={d.date}
                  className="py-2 rounded bg-parchment"
                  title={d.summary}
                >
                  <div className="text-[10px] uppercase tracking-wide text-ink-soft">
                    {i === 0 ? 'Today' : wd}
                  </div>
                  <div className="text-lg leading-none mt-1" aria-hidden>
                    {GLYPH[d.code]}
                  </div>
                  <div className="font-mono text-[11px] mt-1">
                    {d.maxC}° <span className="text-ink-soft">/ {d.minC}°</span>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
